import { ApiError } from '@kirin/api-client/errors';
import { InviteErrorCode } from '@kirin/api-client/contracts/organizations';

/**
 * Reads the machine-readable domain code off an API error body.
 *
 * It lives in `code`, NOT `error`: the backend error handler sends
 * `{ error: <Fastify error name>, message, code: <domain code> }`, so `error`
 * on a 403 is the generic string `"Forbidden"` and only `code` carries
 * `INVITE_IDENTITY_UNVERIFIED`. Reading the wrong field silently classified
 * every 403 as terminal.
 */
function errorCode(error: ApiError): string | null {
  const data = error.data;
  if (data && typeof data === 'object' && 'code' in data) {
    const code = (data as { code?: unknown }).code;
    return typeof code === 'string' ? code : null;
  }
  return null;
}

/**
 * Client errors that make an invite permanently unanswerable, so retrying is
 * futile (KIR-4166):
 *
 * - **404** — the invite is gone (already used, rejected, or expired).
 * - **400** — the token is malformed and will never resolve.
 * - **403 `INVITE_EMAIL_MISMATCH`** — the signed-in identity is verifiably not
 *   the invited one. This is the reported case: an invite addressed to a
 *   `user+tag@` subaddress is delivered to the `user@` mailbox, but Descope
 *   registers the subaddress as a separate identity, so the person who actually
 *   received the link can never satisfy the identity gate — accept *or* reject.
 *
 * A 403 carrying `INVITE_IDENTITY_UNVERIFIED` is deliberately **excluded**: it
 * means the identity could not be confirmed (Descope blip, or an address the
 * user has yet to verify), which retrying or verifying can fix. Treating it as
 * terminal would discard a perfectly valid invite on a transient failure —
 * matching the backend's own `DEFINITIVE_MISMATCH_REASONS` split.
 *
 * This matters because the deep-link modal is `mandatory` (undismissable) and
 * its token is persisted in `localStorage`. Without treating these as terminal,
 * a permanently-403 invite reopens an unanswerable modal on every page load
 * forever. Transient failures (network, 5xx) are deliberately excluded — those
 * must keep the invite alive so a single blip doesn't discard it (KIR-3786).
 */
export function isTerminalInviteError(error: unknown): boolean {
  if (!(error instanceof ApiError)) {
    return false;
  }
  if (error.status === 403) {
    return errorCode(error) !== InviteErrorCode.IdentityUnverified;
  }
  return error.status === 400 || error.status === 404;
}
